import { useState, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Box,
  CardHeader,
  CardContent,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
// components
import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
// sections
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page } from '../../../components';
import { fDate } from '../../../utils/formatTime';
import { getBranches, resetBranchState } from '../store/branchActions';
import { BranchForm, BranchEditForm, BranchDeleteDialog } from '../components';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'name', label: 'Branch Name', alignRight: false },
  { id: 'code', label: 'Branch Code', alignRight: false },
  { id: 'country_code', label: 'Country Code', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'created_at', label: 'Created Date', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

// ----------------------------------------------------------------------

export default function Branches() {
  const dispatch = useDispatch();
  const { isLoading, branches, isSuccess } = useSelector((state) => state.branchReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleEditClick = (branch) => {
    setSelectedBranch(branch);
    handleOpenEditModal();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
  };

  const handleOpenEditModal = () => {
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedBranch(null);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedBranch(null);
  };

  useEffect(() => {
    const payload = {
      page: page + 1,
      rowsPerPage,
    };
    dispatch(getBranches(payload));
  }, [dispatch, page, rowsPerPage]);

  // Refetch branches when operations are successful
  useEffect(() => {
    if (isSuccess) {
      const payload = {
        page: page + 1,
        rowsPerPage,
      };
      dispatch(getBranches(payload));
      // Reset the success state to prevent continuous refetching
      dispatch(resetBranchState());
    }
  }, [isSuccess, dispatch, page, rowsPerPage]);

  // Filter branches based on search term
  const filteredBranches =
    branches?.filter(
      (branch) =>
        branch.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.country_code?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // Helper function to get country name from code
  const getCountryName = (countryCode) => {
    switch (countryCode) {
      case '254':
        return 'Kenya';
      case '255':
        return 'Uganda';
      case '256':
        return 'Tanzania';
      default:
        return countryCode;
    }
  };

  return (
    <Page title="Branches">
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header Section */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              {/* <PlaceIcon sx={{ fontSize: 32, color: 'primary.main' }} /> */}
              <div>
                <Typography variant="h4" gutterBottom>
                  {''}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  Manage your Pesapal branches and locations
                </Typography> */}
              </div>
            </Stack>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddModal} sx={{ borderRadius: 2 }}>
              Add Branch
            </Button>
          </Stack>

          {/* Stats Cards */}
          {/* <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" gutterBottom>
                  {branches?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Locations
                </Typography>
              </Card>
            </Grid>
          </Grid> */}

          {/* Branches Table */}
          <Card>
            <CardHeader
              title="Locations List"
              subheader={`Showing ${filteredBranches.length} of ${branches?.length || 0} branches`}
              action={
                <TextField
                  size="small"
                  placeholder="Search locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
              }
            />
            <CardContent>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <ThreeDots
                        height="80"
                        width="80"
                        radius="9"
                        color="#2065d1"
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible
                      />
                    </Box>
                  ) : (
                    <Table size="medium">
                      <UserListHead headLabel={TABLE_HEAD} rowCount={filteredBranches.length} />
                      <TableBody>
                        {filteredBranches.length > 0 ? (
                          filteredBranches.map((branch) => (
                            <TableRow hover key={branch?.id} tabIndex={-1}>
                              <TableCell component="th" scope="row" sx={{ pl: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Typography variant="subtitle2" noWrap>
                                    {branch?.name}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              <TableCell align="left">
                                <Chip label={branch?.code} size="small" variant="outlined" color="primary" />
                              </TableCell>

                              <TableCell align="left">
                                <Chip
                                  label={`${getCountryName(branch?.country_code)} (${branch?.country_code})`}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              </TableCell>

                              <TableCell align="left">
                                <Label color={(branch?.is_active && 'success') || 'error'}>
                                  {(branch?.is_active && 'Active') || 'Inactive'}
                                </Label>
                              </TableCell>

                              <TableCell align="left">
                                <Typography variant="body2" color="text.secondary">
                                  {branch?.date_created ? fDate(branch.date_created) : 'N/A'}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                {/* <IconButton
                                  size="large"
                                  color="inherit"
                                  onClick={(event) => handleOpenMenu(event, branch)}
                                >
                                  <MoreVertIcon />
                                </IconButton> */}

                                <IconButton
                                  size="large"
                                  color="inherit"
                                  onClick={() => handleEditClick(branch)}
                                  title="Edit Location"
                                >
                                  <EditIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                              <Alert severity="info">
                                {searchTerm
                                  ? 'No branches match your search criteria.'
                                  : 'No branches found. Click "Add Branch" to create your first branch.'}
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={branches?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Stack>

        {/* Add Branch Modal */}
        <BranchForm open={openAddModal} onClose={handleCloseAddModal} />

        {/* Edit Branch Modal */}
        <BranchEditForm open={openEditModal} onClose={handleCloseEditModal} branch={selectedBranch} />

        {/* Delete Confirmation Dialog */}
        <BranchDeleteDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} branch={selectedBranch} />
      </Container>
    </Page>
  );
}
